import React, { useState } from "react";
import {
  Stack,
  Paper,
  Button,
  Text,
  Grid,
  Group,
  Box,
  Alert,
  Flex,
} from "@mantine/core";
import {
  IconClipboardCheck,
  IconClock,
  IconAlertCircle,
} from "@tabler/icons-react";
import { useSalon } from "../../context/SalonContext";
import { useResponsive } from "../../context/ResponsiveContext";
import { usePayrollPeriod } from "../../hooks/usePayrollPeriod";
import { Employee } from "../../types/salon";
import { BusinessInfo } from "../../types/context";
import { TimeTrackingState, TimesheetData } from "../../types/timeTracking";
import StaffSelectionView from "./StaffSelectionView";
import TimeClockView from "./TimeClockView";
import ApproveTimesheetView from "./ApproveTimesheetView";
import BrandedLoadingOverlay from "../common/BrandedLoadingOverlay";

interface SalonContextValue {
  currentUser: Employee | null;
  businessInfo: BusinessInfo | null;
  employees: Employee[];
  isLoading: boolean;
}

type EmployeeStatusMap = Map<string, TimeTrackingState>;

type TimesheetSection = "approve" | "clock";

interface PayrollTimesheetTabProps {
  employeeStatuses: EmployeeStatusMap;
  isLoadingStatuses: boolean;
  onStatusUpdate: (employeeId: string, newStatus: TimeTrackingState) => void;
  timesheetData: TimesheetData;
  isLoadingTimesheet: boolean;
  onTimesheetRefresh: () => Promise<TimesheetData>;
  companyId: string;
  payPeriodId: string;
}

export default function PayrollTimesheetTab({
  employeeStatuses,
  isLoadingStatuses,
  onStatusUpdate,
  timesheetData,
  isLoadingTimesheet,
  onTimesheetRefresh,
  companyId: propCompanyId,
  payPeriodId: propPayPeriodId,
}: PayrollTimesheetTabProps) {
  const { isMobile } = useResponsive();
  const salonContext = useSalon() as unknown as SalonContextValue;
  const {
    currentUser,
    businessInfo,
    employees,
    isLoading: salonLoading,
  } = salonContext;

  const typedBusinessInfo = businessInfo as BusinessInfo | null;
  const typedEmployees = employees as Employee[];

  const companyId = typedBusinessInfo?.rollfi__company_id || "";
  const timezone = typedBusinessInfo?.timezone || "America/New_York";

  // Get current pay period for timesheet view
  const payrollPeriod = usePayrollPeriod(typedBusinessInfo);

  const isOwnerOrManager =
    currentUser?.role === "Owner" || currentUser?.role === "Manager";

  // State for managers - default to approve timesheet section
  const [activeSection, setActiveSection] =
    useState<TimesheetSection>("approve");
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(
    null,
  );

  // Filter employees who have a RollFi user ID attached
  const eligibleEmployees = (typedEmployees || []).filter(
    (emp) => emp.rollfi__user_id,
  );

  // Auto-select first employee if none selected
  React.useEffect(() => {
    if (
      isOwnerOrManager &&
      eligibleEmployees.length > 0 &&
      !selectedEmployee &&
      !isLoadingStatuses
    ) {
      setSelectedEmployee(eligibleEmployees[0]);
    }
  }, [
    isOwnerOrManager,
    eligibleEmployees,
    selectedEmployee,
    isLoadingStatuses,
  ]);

  const handleSelectEmployee = (employee: Employee) => {
    setSelectedEmployee(employee);
  };

  // Menu items for managers
  const menuItems = [
    {
      id: "approve" as TimesheetSection,
      label: "Approve Timesheet",
      icon: <IconClipboardCheck size={18} />,
    },
    {
      id: "clock" as TimesheetSection,
      label: "Clock In/Out Staff",
      icon: <IconClock size={18} />,
    },
  ];

  // For employees, show only their time clock
  if (!isOwnerOrManager) {
    if (!currentUser?.rollfi__user_id) {
      return (
        <Alert color="yellow" title="Not Set Up" icon={<IconAlertCircle />}>
          Your account is not set up for time tracking. Please contact your
          manager to be added to payroll.
        </Alert>
      );
    }

    return (
      <TimeClockView
        employee={currentUser}
        companyId={propCompanyId || companyId}
        timezone={timezone}
        payPeriodId={propPayPeriodId || payrollPeriod?.payPeriodId}
        showBackButton={false}
      />
    );
  }

  // Desktop layout for managers - side nav + content
  if (!isMobile) {
    return (
      <Flex w="100%" gap="md">
        <Paper p="xs" withBorder py="sm" w="100%" maw={240}>
          <Stack gap="sm">
            {menuItems.map((item) => (
              <Button
                key={item.id}
                variant="subtle"
                bg={
                  activeSection === item.id
                    ? "var(--mantine-color-teal-6)"
                    : "var(--mantine-color-white)"
                }
                onClick={() => setActiveSection(item.id)}
                fullWidth
                p="xs"
                styles={{
                  label: {
                    flex: 1,
                    textAlign: "left",
                  },
                }}
              >
                <Text
                  c={activeSection === item.id ? "white" : "#333"}
                  size="md"
                  mt={6}
                >
                  {item.icon}
                </Text>
                <Text
                  ml="xs"
                  size="sm"
                  c={activeSection === item.id ? "white" : "#333"}
                  fw={500}
                >
                  {item.label}
                </Text>
              </Button>
            ))}
          </Stack>
        </Paper>
        <Paper
          p="md"
          withBorder
          style={{ minHeight: "400px", overflowX: "scroll" }}
          w="100%"
        >
          {/* Approve Timesheet - hidden when not active */}
          <Box
            style={{
              display: activeSection === "approve" ? "block" : "none",
            }}
          >
            {payrollPeriod && (
              <ApproveTimesheetView
                eligibleEmployees={eligibleEmployees}
                timezone={timezone}
                timesheetData={timesheetData}
                isLoading={isLoadingTimesheet}
                companyId={propCompanyId || companyId}
                payPeriodId={propPayPeriodId || payrollPeriod.payPeriodId}
                onRefresh={onTimesheetRefresh}
              />
            )}
          </Box>

          {/* Clock In/Out - hidden when not active */}
          <Box
            style={{
              display: activeSection === "clock" ? "flex" : "none",
              height: "100%",
            }}
          >
            {/* Left Panel - Employee List */}
            <Box style={{ flexShrink: 0 }}>
              <StaffSelectionView
                employees={eligibleEmployees}
                employeeStatuses={employeeStatuses}
                selectedEmployeeId={selectedEmployee?.id || null}
                isLoading={salonLoading}
                onSelectEmployee={handleSelectEmployee}
              />
            </Box>

            {/* Right Panel - Time Clock */}
            <Box style={{ flex: 1 }}>
              {selectedEmployee ? (
                <TimeClockView
                  employee={selectedEmployee}
                  companyId={propCompanyId || companyId}
                  timezone={timezone}
                  payPeriodId={propPayPeriodId || payrollPeriod?.payPeriodId}
                  showBackButton={false}
                  isLoadingStatus={isLoadingStatuses}
                  initialStatus={employeeStatuses.get(selectedEmployee.id)}
                  onStatusUpdate={(status) =>
                    onStatusUpdate(selectedEmployee.id, status)
                  }
                />
              ) : (
                <BrandedLoadingOverlay visible={true} />
              )}
            </Box>
          </Box>
        </Paper>
      </Flex>
    );
  }

  // Mobile layout for managers - horizontal tabs + stacked content
  return (
    <Stack gap="md">
      <Paper p="xs" withBorder>
        <Group gap="xs" justify="center" wrap="wrap">
          {menuItems.map((item) => (
            <Button
              key={item.id}
              variant="subtle"
              bg={
                activeSection === item.id
                  ? "var(--mantine-color-teal-6)"
                  : "var(--mantine-color-white)"
              }
              onClick={() => setActiveSection(item.id)}
              size="xs"
            >
              <Text c={activeSection === item.id ? "white" : "#333"} size="xs">
                {item.icon}
              </Text>
              <Text
                ml="xs"
                size="xs"
                c={activeSection === item.id ? "white" : "#333"}
                fw={500}
              >
                {item.label}
              </Text>
            </Button>
          ))}
        </Group>
      </Paper>

      {/* Approve Timesheet - hidden when not active */}
      <Box
        style={{
          display: activeSection === "approve" ? "block" : "none",
        }}
      >
        {payrollPeriod && (
          <ApproveTimesheetView
            eligibleEmployees={eligibleEmployees}
            timezone={timezone}
            timesheetData={timesheetData}
            isLoading={isLoadingTimesheet}
            companyId={propCompanyId || companyId}
            payPeriodId={propPayPeriodId || payrollPeriod.payPeriodId}
            onRefresh={onTimesheetRefresh}
          />
        )}
      </Box>

      {/* Clock In/Out - hidden when not active */}
      <Box
        style={{
          display: activeSection === "clock" ? "block" : "none",
        }}
      >
        <Stack gap="md">
          {/* Employee List */}
          <StaffSelectionView
            employees={eligibleEmployees}
            employeeStatuses={employeeStatuses}
            selectedEmployeeId={selectedEmployee?.id || null}
            isLoading={salonLoading}
            onSelectEmployee={handleSelectEmployee}
          />

          {/* Time Clock for selected employee */}
          {selectedEmployee && (
            <Paper p="md" withBorder>
              <TimeClockView
                employee={selectedEmployee}
                companyId={companyId}
                timezone={timezone}
                showBackButton={false}
                isLoadingStatus={isLoadingStatuses}
                initialStatus={employeeStatuses.get(selectedEmployee.id)}
                onStatusUpdate={(status) =>
                  onStatusUpdate(selectedEmployee.id, status)
                }
              />
            </Paper>
          )}
        </Stack>
      </Box>
    </Stack>
  );
}
